import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Stack, Typography } from '@mui/material';
import { PromptDialogOnSide } from 'common/components/PromptDialogOnSide/PromptDialogOnSide';
import { styles } from 'common/components/PromptDialogOnSide/PromptDialogOnSide.styles';
import { FormRadioSelect } from 'common/form/components/FormRadioSelect';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import { TransactionGroupPeriodTypeCode } from 'generated/graphql';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

interface FormValues {
  periodType: {
    periodTypeCode?: TransactionGroupPeriodTypeCode | null;
    periodCount?: number | null;
  };
}

export interface PeriodType {
  periodTypeCode?: TransactionGroupPeriodTypeCode | null;
  periodCount?: number | null;
}

export interface EditCashFlowAdjustmentPeriodTypeDialogProps
  extends DialogComponentProps<PeriodType | undefined | null> {
  periodType?: PeriodType | null;
}

export const EditCashFlowAdjustmentPeriodTypeDialog: React.FC<EditCashFlowAdjustmentPeriodTypeDialogProps> = ({
  isOpen,
  onReject,
  onResolve,
  periodType,
}) => {
  const { i18n, t } = useTranslation();

  const schema: yup.SchemaOf<FormValues> = yup.object({
    periodType: yup.object({
      periodCount: yup
        .number()
        .nullable(true)
        .integer(t('form.validator.integer'))
        .min(1, t('form.validator.min', { value: 1 }))
        .required(i18n.t('form.validator.fieldIsRequired')),
      periodTypeCode: yup
        .mixed<TransactionGroupPeriodTypeCode>()
        .nullable(true)
        .required(i18n.t('form.validator.fieldIsRequired')),
    }),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      periodType: {
        periodCount: periodType?.periodCount,
        periodTypeCode: periodType?.periodTypeCode,
      },
    } as FormValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    onResolve({ periodCount: data.periodType.periodCount, periodTypeCode: data.periodType.periodTypeCode });
  });

  const periodicityOptions = usePeriodicityOptions();

  return (
    <PromptDialogOnSide
      onClose={() => onReject()}
      open={isOpen}
      title={t('fintelligenceReport.editCashFlowAdjustmentPeriodTypeDialog.title')}
    >
      <FormProvider {...formMethods}>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Box>
            <FormRadioSelect name="periodType" options={periodicityOptions} />
          </Box>

          <Stack mt={3} spacing={1}>
            <Button color="primary" sx={styles.button} type="submit">
              <Typography color="white" variant="bodyL">
                {t('fintelligenceReport.editCashFlowAdjustmentPeriodTypeDialog.save')}
              </Typography>
            </Button>
            <Button color="tertiaryButton" onClick={() => onReject()} sx={styles.button}>
              <Typography color="blue.dark" variant="bodyL">
                {t('common:cancel')}
              </Typography>
            </Button>
          </Stack>
        </Box>
      </FormProvider>
    </PromptDialogOnSide>
  );
};
